import { useEffect, useRef, useState } from 'react'
import { useParams, useLocation, Link } from 'react-router-dom'
import { useAppSelector } from '../../store'
import { chatApi } from '../../api/chatApi'
import { subscribeToMessages, sendMessage } from '../../firebase/chatService'
import { ChatMessage } from '../../types'
import { ArrowLeft, Send, Loader2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { format } from 'date-fns'
import { signInWithCustomToken } from 'firebase/auth'
import { firebaseAuth } from '../../firebase/firebaseConfig'

export default function ConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>()
  const location = useLocation()
  const otherUser = location.state?.otherUser
  const { user } = useAppSelector((s) => s.auth)

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Get Firebase custom token and authenticate
    chatApi
      .getFirebaseToken()
      .then(({ data }) => {
        if (!firebaseAuth) throw new Error('Firebase not initialized')
        return signInWithCustomToken(firebaseAuth, data.data!.token)
      })
      .then(() => {
        setLoading(false)
        // Subscribe to Firestore messages
        const unsub = subscribeToMessages(conversationId!, (msgs) => {
          setMessages(msgs)
          setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
        })
        return () => unsub()
      })
      .catch(() => {
        toast.error('Failed to load chat')
        setLoading(false)
      })
  }, [conversationId])

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!text.trim() || sending) return
    setSending(true)
    try {
      await sendMessage(conversationId!, text.trim(), user!.id)
      setText('')
    } catch {
      toast.error('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-80px)]">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <Link to="/chat" className="p-1 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={20} />
        </Link>
        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
          {otherUser?.profilePicUrl ? (
            <img src={otherUser.profilePicUrl} alt={otherUser.displayName} className="w-full h-full object-cover" />
          ) : (
            <span className="font-medium text-primary-700">
              {otherUser?.displayName?.[0]?.toUpperCase() || '?'}
            </span>
          )}
        </div>
        <p className="font-semibold text-gray-900">{otherUser?.displayName || 'Chat'}</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={24} className="animate-spin text-primary-600" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">Start the conversation!</p>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderId === user?.id
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl text-sm ${
                    isMe
                      ? 'bg-primary-600 text-white rounded-br-sm'
                      : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                  }`}
                >
                  <p>{msg.text}</p>
                  <p className={`text-xs mt-1 ${isMe ? 'text-primary-200' : 'text-gray-400'}`}>
                    {format(new Date(msg.timestamp), 'HH:mm')}
                  </p>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-3 pt-4 border-t border-gray-200">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message..."
          className="input flex-1"
          autoFocus
        />
        <button type="submit" disabled={!text.trim() || sending} className="btn-primary px-4">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  )
}
